import { ExpandMore, Settings } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Alert, Autocomplete, Box, Checkbox, Divider, FormControlLabel, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { RecordManagement } from "app/class/record-management";
import SysColumn from "app/class/sys-column";
import SysTable from "app/class/sys-table";
import { useAuth } from "app/providers/AuthProvider";
import { BuilderFieldDef, BuilderStep, FormBuilderState, TargetColumn } from "../../types/formBuilder.types";
import { getFieldTypeDefinition } from "../shared/FieldTypeRegistry";
import TranslatableInput from "component/global/translation/TranslatableInput";
import ChoiceOptionsEditor from "./ChoiceOptionsEditor";
import ColumnMapper from "./ColumnMapper";
import RuleBuilder from "./RuleBuilder";
import FilterEditor from "./FilterEditor";
import { ReferenceAutocompleteInput } from "modules/record/shared/components/ReferenceAutocompleteInput";
import { useReferenceDisplayColumn, useReferenceOptions } from "modules/record/shared/hooks/useReferenceOptions";
import { useCurrencyPreferences } from "modules/record/shared/hooks/useCurrencyPreferences";
import { useChoiceOptions } from "modules/record/shared/hooks/useChoiceOptions";
import { resolveDisplayText, translateMetadataLabel } from "modules/record/shared/services/recordMetadataLabels";
import queryKeys from "shared/services/queryKeys";

interface PropertyPanelProps {
    state: FormBuilderState;
    selectedField: BuilderFieldDef | null;
    columns: TargetColumn[];
    tables: SysTable[];
    allColumns: SysColumn[];
    onFormPropsChange: (props: Partial<Pick<FormBuilderState, "targetTable" | "onSubmit" | "successMessage" | "successRedirect" | "recordId" | "eventsBinding" | "categoryId" | "accessType" | "accessRoles" | "accessGroupIds" | "publicAccessMode" | "active" | "published" | "requestStatusField" | "requestStatusMappingJson">>) => void;
    onFieldChange: (name: string, updates: Partial<BuilderFieldDef>, labelText?: string) => void;
    onFieldRulesChange: (name: string, rules: Partial<BuilderFieldDef["rules"]>) => void;
    onStepChange: (index: number, updates: Partial<BuilderStep>) => void;
    onPreviewTranslation?: (key: string, text: string) => void;
}

const translationBase = (state: FormBuilderState, suffix: string) => `sp.forms.${state.translationId}.${suffix}`;
const textLengthFieldTypes = new Set(["text", "textarea", "email", "url", "password", "richtext"]);
const dateInputType = (type: string) => type === "datetime" ? "datetime-local" : type;
const validDateLimit = (type: string, value: string) => {
    if (!value) return true;
    if (type === "time") return /^([01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/.test(value);
    const match = value.match(type === "date"
        ? /^(\d{4})-(\d{2})-(\d{2})$/
        : /^(\d{4})-(\d{2})-(\d{2})T([01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/);
    if (!match) return false;
    const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
    return date.getUTCFullYear() === Number(match[1]) && date.getUTCMonth() === Number(match[2]) - 1 && date.getUTCDate() === Number(match[3]);
};
const requestStateKeys = ["open", "in_progress", "closed", "cancelled"] as const;
const defaultRequestStatusMapping = {
    open: ["new", "open", "todo"],
    in_progress: ["in_progress", "doing"],
    closed: ["closed", "resolved", "done"],
    cancelled: ["cancelled", "canceled"],
};

const parseStatusMapping = (value: string): Record<string, string[]> => {
    try {
        const parsed = JSON.parse(value || "{}");
        if (parsed && typeof parsed === "object") {
            return requestStateKeys.reduce((acc, state) => ({
                ...acc,
                [state]: Array.isArray(parsed[state]) ? parsed[state].map(String).filter(Boolean) : [],
            }), {} as Record<string, string[]>);
        }
    } catch (_error) {
        // Invalid JSON is treated as empty mapping in the visual editor.
    }
    return { ...defaultRequestStatusMapping };
};

const serializeStatusMapping = (mapping: Record<string, string[]>): string => JSON.stringify(mapping, null, 2);

interface RoleRecord {
    id: string;
    name?: string | null;
    label?: string | null;
    title?: string | null;
}

interface GroupRecord {
    id: string;
    name?: string | null;
    label?: string | null;
    title?: string | null;
}

interface CategoryRecord {
    id: string;
    name?: string | null;
    title?: string | null;
}

const normalizeRecordList = <T,>(response: any): T[] => {
    const records = Array.isArray(response) ? response : response?.data || response?.records || [];
    return Array.isArray(records) ? records as T[] : [];
};

const parseAccessList = (value: string): string[] => {
    if (!value) return [];
    try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return parsed.map((item) => String(item)).filter(Boolean);
    } catch (_error) {
        // Keep backward compatibility with older comma-separated values.
    }
    return String(value).split(",").map((item) => item.trim()).filter(Boolean);
};

const serializeAccessList = (value: string[]): string => value.length ? JSON.stringify(value) : "";

const roleValue = (role: RoleRecord): string => role.name || role.id;
const categoryLabel = (category: CategoryRecord): string => category.title || category.name || category.id;

type AccordionSection = "form" | "portalTracking" | "fieldOrStep";
type AccordionExpandedState = Record<AccordionSection, boolean>;
const DEFAULT_ACCORDION_STATE: AccordionExpandedState = { form: true, portalTracking: true, fieldOrStep: true };
const ACCORDION_TABLE = "service_builder";
const ACCORDION_PREFERENCE_KEY = "service_builder.property_panel_accordions";

const ReferenceDefaultValueField = ({ field, onChange }: { field: BuilderFieldDef; onChange: (value: string) => void }) => {
    const { t } = useTranslation();
    const [search, setSearch] = useState("");
    const { displayColumnName } = useReferenceDisplayColumn(field.source || undefined);
    const options = useReferenceOptions({
        tableName: field.source || undefined,
        selectedId: field.defaultValue || null,
        search,
        displayColumnName: field.display || displayColumnName,
        evaluatedFilters: field.referenceFilter || null,
    });

    if (!field.source) {
        return <TextField size="small" label={t("Default value")} value={field.defaultValue} onChange={(event) => onChange(event.target.value)} helperText={t("Select a source table to search records.")} fullWidth />;
    }

    return (
        <Box>
            <Typography className="service-builder__field-helper">{t("Default value")}</Typography>
            <ReferenceAutocompleteInput
                options={options.data ?? []}
                loading={options.isLoading}
                selectedId={field.defaultValue || null}
                displayField={field.display || displayColumnName}
                referenceTable={field.source}
                onChange={(value) => onChange(value || "")}
                onSearchChange={setSearch}
                showOpenButton
            />
        </Box>
    );
};

const PropertyPanel = ({ state, selectedField, columns, tables, allColumns, onFormPropsChange, onFieldChange, onFieldRulesChange, onStepChange, onPreviewTranslation }: PropertyPanelProps) => {
    const { t } = useTranslation();
    const tableLabel = (table: SysTable) => `${translateMetadataLabel(t, table) || table.label || table.name} (${table.name})`;
    const columnLabel = (column: SysColumn | TargetColumn) => `${translateMetadataLabel(t, column) || column.label || column.name} (${column.name})`;
    // sys_roles has no label/title column at all today (role.label/.title are always undefined,
    // this always falls through to the technical role.name) and sys_groups.label is plain TEXT,
    // not TRANSLATABLE_TEXT — but its values are in practice translation keys set by platform
    // packages (e.g. "demo.access_request.group.service_desk"), same latent bug as PRIM-004.
    // resolveDisplayText is safe here regardless: literal text (or an untranslated key) passes
    // through unchanged.
    const roleLabel = (role: RoleRecord): string => String(resolveDisplayText(t, role.label || role.title || role.name || role.id));
    const groupLabel = (group: GroupRecord): string => String(resolveDisplayText(t, group.label || group.title || group.name || group.id));
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const currencyPreferences = useCurrencyPreferences();
    const [expandedSections, setExpandedSections] = useState<AccordionExpandedState>(DEFAULT_ACCORDION_STATE);
    const selectedSectionRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!selectedField) return;
        setExpandedSections((current) => current.fieldOrStep ? current : { ...current, fieldOrStep: true });
        const frame = requestAnimationFrame(() => selectedSectionRef.current?.scrollIntoView?.({ block: "nearest" }));
        return () => cancelAnimationFrame(frame);
    }, [selectedField?.name]);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const expandedAppliedRef = useRef(false);
    const accordionQueryKey = queryKeys.records.lookup("sys_user_preference", "form-builder-accordion", ACCORDION_TABLE, ACCORDION_PREFERENCE_KEY, user?.id ?? null);
    const accordionPreferenceQuery = useQuery({
        queryKey: accordionQueryKey,
        enabled: Boolean(user?.id),
        queryFn: async () => {
            const response = await new RecordManagement("sys_user_preference")
                .where("table_name", ACCORDION_TABLE)
                .where("user_id", user?.id)
                .where("preference_key", ACCORDION_PREFERENCE_KEY)
                .first();

            if (!response?.preference_value) return null;
            return typeof response.preference_value === "string" ? JSON.parse(response.preference_value) as Partial<AccordionExpandedState> : response.preference_value as Partial<AccordionExpandedState>;
        },
    });
    const saveAccordionPreference = useMutation({
        mutationFn: async (next: AccordionExpandedState) => {
            if (!user?.id) return;
            const payload = JSON.stringify(next);
            const existing = await new RecordManagement("sys_user_preference")
                .where("table_name", ACCORDION_TABLE)
                .where("user_id", user.id)
                .where("preference_key", ACCORDION_PREFERENCE_KEY)
                .first();

            if (existing?.id) {
                await new RecordManagement("sys_user_preference").update(existing.id, { preference_value: payload });
                return;
            }

            await new RecordManagement("sys_user_preference").save({
                user_id: user.id,
                table_name: ACCORDION_TABLE,
                preference_key: ACCORDION_PREFERENCE_KEY,
                preference_value: payload,
            });
        },
        onSuccess: (_, next) => queryClient.setQueryData(accordionQueryKey, next),
    });

    useEffect(() => {
        if (expandedAppliedRef.current || accordionPreferenceQuery.isLoading) return;
        expandedAppliedRef.current = true;
        if (accordionPreferenceQuery.data) {
            setExpandedSections((current) => ({ ...current, ...accordionPreferenceQuery.data }));
        }
    }, [accordionPreferenceQuery.data, accordionPreferenceQuery.isLoading]);

    const toggleSection = (section: AccordionSection) => (_event: unknown, isExpanded: boolean) => {
        const next = { ...expandedSections, [section]: isExpanded };
        setExpandedSections(next);
        saveAccordionPreference.mutate(next);
    };

    const definition = selectedField ? getFieldTypeDefinition(selectedField.type) : null;
    const selectedStepIndex = selectedField ? null : state.activeStepIndex;
    const selectedStep = typeof selectedStepIndex === "number" ? state.steps[selectedStepIndex] || null : null;
    const selectedTargetTable = tables.find((table) => table.name === state.targetTable || table.id === state.targetTable) || null;
    const selectedSourceTable = selectedField ? tables.find((table) => table.name === selectedField.source || table.id === selectedField.source) || null : null;

    const sourceColumns = useMemo(() => {
        if (!selectedSourceTable) return [];
        return allColumns.filter((column) => column.sys_table_id === selectedSourceTable.id);
    }, [allColumns, selectedSourceTable]);
    const selectedDisplayColumn = selectedField ? sourceColumns.find((column) => column.name === selectedField.display) || null : null;
    const currencyOptions = currencyPreferences.data.currencies;
    const selectedCurrency = selectedField ? currencyOptions.find((currency) => currency.code === selectedField.currencyCode || currency.id === selectedField.currencyCode) || null : null;
    const currencyFieldOptions = selectedField ? state.fields.filter((field) => field.name !== selectedField.name) : [];
    const formFieldOptions = selectedField
        ? state.fields
            .filter((field) => field.name !== selectedField.name)
            .map((field) => ({ name: field.name, label: String(resolveDisplayText(t, field.label) || field.name) }))
        : [];
    const selectedCurrencyField = selectedField ? currencyFieldOptions.find((field) => field.name === selectedField.currencyField) || null : null;
    const rolesQuery = useQuery({
        queryKey: ["sys_roles", "service-builder-access"],
        queryFn: async () => normalizeRecordList<RoleRecord>(await new RecordManagement<RoleRecord>("sys_roles").orderBy("name", "asc").run()),
        staleTime: 5 * 60 * 1000,
    });
    const selectedRoleValues = parseAccessList(state.accessRoles);
    const selectedRoles = (rolesQuery.data || []).filter((role) => selectedRoleValues.includes(roleValue(role)) || selectedRoleValues.includes(role.id));
    const groupsQuery = useQuery({
        queryKey: ["sys_groups", "service-builder-access"],
        queryFn: async () => normalizeRecordList<GroupRecord>(await new RecordManagement<GroupRecord>("sys_groups").orderBy("name", "asc").run()),
        staleTime: 5 * 60 * 1000,
    });
    const selectedGroups = (groupsQuery.data || []).filter((group) => (state.accessGroupIds || []).includes(group.id));
    const categoriesQuery = useQuery({
        queryKey: ["sys_sp_categories", "service-builder-category"],
        queryFn: async () => normalizeRecordList<CategoryRecord>(await new RecordManagement<CategoryRecord>("sys_sp_categories").orderBy("sort_order", "asc").run()),
        staleTime: 5 * 60 * 1000,
    });
    const selectedCategory = (categoriesQuery.data || []).find((category) => category.id === state.categoryId) || null;
    const statusColumnSuggestion = columns.find((column) => ["status", "state"].includes(column.name));
    const selectedStatusColumn = columns.find((column) => column.name === state.requestStatusField) || null;
    const selectedStatusSysColumn = selectedStatusColumn ? allColumns.find((column) => column.id === selectedStatusColumn.id || (column.name === selectedStatusColumn.name && (!selectedTargetTable || column.sys_table_id === selectedTargetTable.id))) : undefined;
    const statusChoiceOptions = useChoiceOptions(selectedStatusSysColumn);
    const statusMapping = useMemo(() => parseStatusMapping(state.requestStatusMappingJson), [state.requestStatusMappingJson]);
    const statusValueOptions = selectedStatusColumn?.columnType === "CHOICE"
        ? (statusChoiceOptions.data ?? []).map((option) => option.value)
        : [];
    const updateStatusMapping = (stateKey: typeof requestStateKeys[number], values: string[]) => {
        const next = { ...statusMapping, [stateKey]: values.map(String).filter(Boolean) };
        onFormPropsChange({ requestStatusMappingJson: serializeStatusMapping(next) });
    };
    const lengthContradiction = Boolean(selectedField && selectedField.minLength !== "" && selectedField.maxLength !== "" && Number(selectedField.minLength) > Number(selectedField.maxLength));
    const hasDateLimits = Boolean(selectedField && getFieldTypeDefinition(selectedField.type)?.supportsMinMax);
    const invalidMinimum = Boolean(hasDateLimits && selectedField && !validDateLimit(selectedField.type, selectedField.minDate));
    const invalidMaximum = Boolean(hasDateLimits && selectedField && !validDateLimit(selectedField.type, selectedField.maxDate));
    const rangeContradiction = Boolean(hasDateLimits && selectedField && !invalidMinimum && !invalidMaximum && selectedField.minDate && selectedField.maxDate && selectedField.minDate > selectedField.maxDate);

    return (
        <Box className="service-builder__properties">
            <Stack direction="row" spacing={1} alignItems="center" className="service-builder__panel-header">
                <Settings fontSize="small" />
                <Typography variant="subtitle2">{t("Properties")}</Typography>
            </Stack>
            <Divider />
            <Box className="service-builder__properties-scroll">
                <Accordion
                    expanded={expandedSections.form}
                    onChange={toggleSection("form")}
                    disableGutters
                    elevation={0}
                    className="service-builder__accordion"
                >
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography className="service-builder__section-title">{t("Form")}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box className="service-builder__accordion-fields">
                            <Autocomplete
                                size="small"
                                options={tables}
                                value={selectedTargetTable}
                                onChange={(_, table) => onFormPropsChange({ targetTable: table?.name || "" })}
                                getOptionLabel={tableLabel}
                                isOptionEqualToValue={(option, selected) => option.id === selected.id}
                                renderInput={(params) => <TextField {...params} label={t("Target table")} helperText={t("Search by display label or internal name.")} />}
                            />
                            <Autocomplete
                                size="small"
                                options={categoriesQuery.data || []}
                                value={selectedCategory}
                                loading={categoriesQuery.isLoading}
                                onChange={(_, category) => onFormPropsChange({ categoryId: category?.id || "" })}
                                getOptionLabel={categoryLabel}
                                isOptionEqualToValue={(option, selected) => option.id === selected.id}
                                renderInput={(params) => <TextField {...params} label={t("Category")} helperText={t("Where this service shows up in the portal's category tree. A restricted category blocks this service even if its own access type is public.")} />}
                            />
                            <TextField size="small" select label={t("Submit mode")} value={state.onSubmit} onChange={(event) => onFormPropsChange({ onSubmit: event.target.value as "create" | "update" | "create_or_update" })} fullWidth>
                                <MenuItem value="create">{t("Create")}</MenuItem>
                                <MenuItem value="update">{t("Update")}</MenuItem>
                                <MenuItem value="create_or_update">{t("Create or Update")}</MenuItem>
                            </TextField>
                            <TextField size="small" select label={t("Access type")} value={state.accessType || "authenticated"} onChange={(event) => onFormPropsChange({ accessType: event.target.value, accessRoles: event.target.value === "roles" ? state.accessRoles : "", accessGroupIds: event.target.value === "groups" ? state.accessGroupIds : [] })} fullWidth>
                                <MenuItem value="public">{t("Public")}</MenuItem>
                                <MenuItem value="authenticated">{t("Authenticated")}</MenuItem>
                                <MenuItem value="roles">{t("Roles")}</MenuItem>
                                <MenuItem value="groups">{t("Groups")}</MenuItem>
                            </TextField>
                            <Stack direction="row" spacing={2}>
                                <FormControlLabel
                                    control={<Checkbox checked={state.active} onChange={(event) => onFormPropsChange({ active: event.target.checked })} />}
                                    label={t("Active")}
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={state.published} onChange={(event) => onFormPropsChange({ published: event.target.checked })} />}
                                    label={t("Published")}
                                />
                            </Stack>
                            {state.accessType === "roles" && (
                                <Autocomplete
                                    multiple
                                    size="small"
                                    options={rolesQuery.data || []}
                                    value={selectedRoles}
                                    loading={rolesQuery.isLoading}
                                    onChange={(_, roles) => onFormPropsChange({ accessRoles: serializeAccessList(roles.map(roleValue)) })}
                                    getOptionLabel={roleLabel}
                                    isOptionEqualToValue={(option, selected) => option.id === selected.id}
                                    renderInput={(params) => <TextField {...params} label={t("Allowed roles")} helperText={t("Users need at least one selected role to see this service in portal widgets.")} />}
                                />
                            )}
                            {state.accessType === "groups" && (
                                <Autocomplete
                                    multiple
                                    size="small"
                                    options={groupsQuery.data || []}
                                    value={selectedGroups}
                                    loading={groupsQuery.isLoading}
                                    onChange={(_, groups) => onFormPropsChange({ accessGroupIds: groups.map((group) => group.id) })}
                                    getOptionLabel={groupLabel}
                                    isOptionEqualToValue={(option, selected) => option.id === selected.id}
                                    renderInput={(params) => <TextField {...params} label={t("Allowed groups")} helperText={t("Users need to belong to at least one selected group to access this service. Always requires authentication.")} />}
                                />
                            )}
                            {state.accessType === "public" && (
                                <TextField
                                    size="small"
                                    select
                                    label={t("Public access mode")}
                                    value={state.publicAccessMode || "both"}
                                    onChange={(event) => onFormPropsChange({ publicAccessMode: event.target.value })}
                                    helperText={t("\"Guest only\" actively blocks logged-in users. \"Anyone\" is today's default, unrestricted behavior.")}
                                    fullWidth
                                >
                                    <MenuItem value="both">{t("Anyone")}</MenuItem>
                                    <MenuItem value="authenticated">{t("Logged-in users only")}</MenuItem>
                                    <MenuItem value="guest">{t("Guests only")}</MenuItem>
                                </TextField>
                            )}
                            <TextField
                                size="small"
                                label={t("Success redirect")}
                                value={state.successRedirect}
                                onChange={(event) => onFormPropsChange({ successRedirect: event.target.value })}
                                helperText={t("Leave empty to open the created request review page.")}
                                fullWidth
                            />
                        </Box>
                    </AccordionDetails>
                </Accordion>

                <Accordion
                    expanded={expandedSections.portalTracking}
                    onChange={toggleSection("portalTracking")}
                    disableGutters
                    elevation={0}
                    className="service-builder__accordion"
                >
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography className="service-builder__section-title">{t("Portal tracking")}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box className="service-builder__accordion-fields">
                            <Autocomplete
                                size="small"
                                options={columns}
                                value={selectedStatusColumn}
                                onChange={(_, column) => onFormPropsChange({ requestStatusField: column?.name || "" })}
                                getOptionLabel={columnLabel}
                                isOptionEqualToValue={(option, selected) => option.name === selected.name}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t("Status field")}
                                        helperText={statusColumnSuggestion && !state.requestStatusField ? t("Suggested field: {{field}}", { field: statusColumnSuggestion.name }) : t("Optional field from the target table used to update portal request status.")}
                                    />
                                )}
                            />
                            {requestStateKeys.map((stateKey) => (
                                <Autocomplete
                                    key={stateKey}
                                    multiple
                                    freeSolo
                                    size="small"
                                    options={statusValueOptions}
                                    value={statusMapping[stateKey] || []}
                                    onChange={(_, values) => updateStatusMapping(stateKey, values)}
                                    getOptionLabel={(value) => {
                                        const label = (statusChoiceOptions.data ?? []).find((option) => option.value === value)?.label;
                                        return String(label ? t(label) : t(value, { defaultValue: value.replace(/_/g, " ") }));
                                    }}
                                    renderInput={(params) => <TextField {...params} label={t("Values for {{state}}", { state: t(`service_portal.request_state.${stateKey}`, { defaultValue: stateKey }) })} />}
                                />
                            ))}
                        </Box>
                    </AccordionDetails>
                </Accordion>

                {(selectedField || (selectedStep && typeof selectedStepIndex === "number")) ? (
                    <Accordion
                        ref={selectedSectionRef}
                        expanded={expandedSections.fieldOrStep}
                        onChange={toggleSection("fieldOrStep")}
                        disableGutters
                        elevation={0}
                        className="service-builder__accordion"
                    >
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography className="service-builder__section-title">{selectedField ? t("Selected Field") : t("Selected Step")}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box className="service-builder__accordion-fields">
                                {selectedField ? (
                                    <>
                                        <TranslatableInput
                                            label="Label"
                                            value={selectedField.label}
                                            suggestedKey={translationBase(state, `${selectedField.uid || selectedField.name}.label`)}
                                            defaultPublic={true}
                                            showTranslationKey={advancedOpen}
                                            onDraftChange={(draft) => onPreviewTranslation?.(translationBase(state, `${selectedField.uid || selectedField.name}.label`), draft)}
                                            onChange={(label, displayValue) => onFieldChange(selectedField.name, { label }, displayValue)}
                                        />
                                        <TranslatableInput
                                            label="Placeholder"
                                            value={selectedField.placeholder}
                                            suggestedKey={translationBase(state, `${selectedField.uid || selectedField.name}.placeholder`)}
                                            defaultPublic={true}
                                            showTranslationKey={advancedOpen}
                                            onChange={(placeholder) => onFieldChange(selectedField.name, { placeholder })}
                                        />
                                        <FormControlLabel
                                            control={<Checkbox checked={selectedField.required} onChange={(event) => onFieldChange(selectedField.name, { required: event.target.checked })} />}
                                            label={t("Required")}
                                        />
                                        <ColumnMapper
                                            value={selectedField.map}
                                            columns={columns}
                                            compatibleColumnTypes={definition?.compatibleColumnTypes || []}
                                            onChange={(map) => onFieldChange(selectedField.name, { map })}
                                        />
                                        {definition?.hasSource && (
                                            <Autocomplete
                                                size="small"
                                                options={tables}
                                                value={selectedSourceTable}
                                                onChange={(_, table) => onFieldChange(selectedField.name, { source: table?.name || "", display: "", valueField: "" })}
                                                getOptionLabel={tableLabel}
                                                isOptionEqualToValue={(option, selected) => option.id === selected.id}
                                                renderInput={(params) => <TextField {...params} label={t("Source table")} helperText={t("Used by dynamic choice/reference fields.")} />}
                                            />
                                        )}
                                        {selectedField.type === "reference" && (
                                            <>
                                                <Autocomplete
                                                    size="small"
                                                    options={sourceColumns}
                                                    value={selectedDisplayColumn}
                                                    onChange={(_, column) => onFieldChange(selectedField.name, { display: column?.name || "" })}
                                                    getOptionLabel={columnLabel}
                                                    isOptionEqualToValue={(option, selected) => option.id === selected.id}
                                                    renderInput={(params) => <TextField {...params} label={t("Display column")} helperText={sourceColumns.length ? t("Column shown to users.") : t("Select a source table first.")} />}
                                                />
                                                <Box className="service-builder__reference-filter">
                                                    <Typography className="service-builder__section-title">{t("Reference filter")}</Typography>
                                                    {sourceColumns.length ? (
                                                        <FilterEditor
                                                            value={selectedField.referenceFilter || null}
                                                            columns={sourceColumns}
                                                            allColumns={allColumns}
                                                            tables={tables}
                                                            formFields={formFieldOptions}
                                                            buttonLabel={t("Edit reference filter")}
                                                            title={t("Reference filter")}
                                                            onChange={(referenceFilter) => onFieldChange(selectedField.name, { referenceFilter })}
                                                        />
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">{t("Select a source table to configure record filters.")}</Typography>
                                                    )}
                                                </Box>
                                            </>
                                        )}
                                        {selectedField.type === "choice" && selectedField.source && (
                                            <Box className="service-builder__reference-filter">
                                                <Typography className="service-builder__section-title">{t("Dropdown filter")}</Typography>
                                                {sourceColumns.length ? (
                                                    <FilterEditor
                                                        value={selectedField.referenceFilter || null}
                                                        columns={sourceColumns}
                                                        allColumns={allColumns}
                                                        tables={tables}
                                                        formFields={formFieldOptions}
                                                        buttonLabel={t("Edit dropdown filter")}
                                                        title={t("Dropdown filter")}
                                                        onChange={(referenceFilter) => onFieldChange(selectedField.name, { referenceFilter })}
                                                    />
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">{t("Select a source table to configure record filters.")}</Typography>
                                                )}
                                            </Box>
                                        )}
                                        {textLengthFieldTypes.has(selectedField.type) && (
                                            <Stack spacing={1}>
                                            <Stack direction="row" spacing={1}>
                                                <TextField size="small" type="number" label={t("Minimum characters")} value={selectedField.minLength} onChange={(event) => onFieldChange(selectedField.name, { minLength: event.target.value })} fullWidth />
                                                <TextField size="small" type="number" label={t("Maximum characters")} value={selectedField.maxLength} onChange={(event) => onFieldChange(selectedField.name, { maxLength: event.target.value })} fullWidth />
                                            </Stack>
                                            {lengthContradiction && <Alert severity="warning">{t("Minimum is greater than maximum; this rule will be ignored on the form.")}</Alert>}
                                            </Stack>
                                        )}

                                        {selectedField.type === "currency" && (
                                            <>
                                                <Autocomplete
                                                    size="small"
                                                    options={currencyOptions}
                                                    value={selectedCurrency}
                                                    loading={currencyPreferences.isLoading}
                                                    onChange={(_, currency) => onFieldChange(selectedField.name, { currencyCode: currency?.code || "" })}
                                                    getOptionLabel={(currency) => currency.code ? currency.code + " (" + (currency.symbol || currency.code) + ")" : ""}
                                                    isOptionEqualToValue={(option, selected) => option.id === selected.id || option.code === selected.code}
                                                    renderInput={(params) => <TextField {...params} label={t("Default currency")} helperText={t("Used when no currency field is selected.")} />}
                                                />
                                                <Autocomplete
                                                    size="small"
                                                    options={currencyFieldOptions}
                                                    value={selectedCurrencyField}
                                                    onChange={(_, field) => onFieldChange(selectedField.name, { currencyField: field?.name || "" })}
                                                    getOptionLabel={(field) => (translateMetadataLabel(t, field) || field.label || field.name) + " (" + field.name + ")"}
                                                    isOptionEqualToValue={(option, selected) => option.name === selected.name}
                                                    renderInput={(params) => <TextField {...params} label={t("Currency field")} helperText={t("Optional field whose value contains the currency code.")} />}
                                                />
                                            </>
                                        )}
                                        {selectedField.type === "choice" && (
                                            <ChoiceOptionsEditor
                                                value={selectedField.choiceItems || []}
                                                suggestedKeyBase={translationBase(state, (selectedField.uid || selectedField.name) + ".choices")}
                                                showTranslationKey={advancedOpen}
                                                onChange={(choiceItems) => onFieldChange(selectedField.name, { choiceItems })}
                                                sourceColumns={sourceColumns}
                                                labelField={selectedField.display}
                                                valueField={selectedField.valueField}
                                                onSourceFieldsChange={(updates) => onFieldChange(selectedField.name, updates)}
                                            />
                                        )}
                                        {definition?.supportsVariant && selectedField.type === "boolean" && (
                                            <TextField size="small" select label={t("Boolean style")} value={selectedField.variant || "switch"} onChange={(event) => onFieldChange(selectedField.name, { variant: event.target.value })} fullWidth>
                                                <MenuItem value="switch">{t("Switch")}</MenuItem>
                                                <MenuItem value="checkbox">{t("service_builder.field.checkbox", { defaultValue: "Checkbox" })}</MenuItem>
                                            </TextField>
                                        )}
                                        {definition?.supportsMinMax && (
                                            <Stack spacing={1}>
                                            <Stack direction={selectedField.type === "datetime" ? "column" : "row"} spacing={1}>
                                                <TextField size="small" type={invalidMinimum ? "text" : dateInputType(selectedField.type)} label={t("Minimum value")} value={selectedField.minDate} onChange={(event) => onFieldChange(selectedField.name, { minDate: event.target.value })} error={invalidMinimum} fullWidth InputLabelProps={{ shrink: true }} />
                                                <TextField size="small" type={invalidMaximum ? "text" : dateInputType(selectedField.type)} label={t("Maximum value")} value={selectedField.maxDate} onChange={(event) => onFieldChange(selectedField.name, { maxDate: event.target.value })} error={invalidMaximum} fullWidth InputLabelProps={{ shrink: true }} />
                                            </Stack>
                                            {(invalidMinimum || invalidMaximum) && <Alert severity="warning">{t("Enter a valid date or time; invalid limits will be ignored on the form.")}</Alert>}
                                            {rangeContradiction && <Alert severity="warning">{t("Minimum is greater than maximum; this rule will be ignored on the form.")}</Alert>}
                                            </Stack>
                                        )}
                                        {selectedField.type === "attachment" && (
                                            <>
                                                <TextField size="small" label={t("Accepted file types")} value={selectedField.accept} onChange={(event) => onFieldChange(selectedField.name, { accept: event.target.value })} helperText={t("Use MIME types or extensions separated by commas, for example image/*,.pdf.")} fullWidth />
                                                <FormControlLabel
                                                    control={<Checkbox checked={selectedField.multiple} onChange={(event) => onFieldChange(selectedField.name, { multiple: event.target.checked })} />}
                                                    label={t("Allow multiple files")}
                                                />
                                            </>
                                        )}
                                        {selectedField.type === "reference" ? (
                                            <ReferenceDefaultValueField field={selectedField} onChange={(defaultValue) => onFieldChange(selectedField.name, { defaultValue })} />
                                        ) : (
                                            <TextField size="small" label={t("Default value")} value={selectedField.defaultValue} onChange={(event) => onFieldChange(selectedField.name, { defaultValue: event.target.value })} fullWidth />
                                        )}
                                        <Accordion expanded={advancedOpen} onChange={(_, expanded) => setAdvancedOpen(expanded)} disableGutters elevation={0}>
                                            <AccordionSummary expandIcon={<ExpandMore />}><Typography>{t("Advanced")}</Typography></AccordionSummary>
                                            <AccordionDetails><Stack spacing={1}>
                                                <TextField size="small" label={t("Internal name")} value={selectedField.name} disabled helperText={t("Generated from label and kept unique.")} fullWidth />
                                                {selectedField.type === "password" && <TextField size="small" label={t("Allowed format (advanced)")} value={selectedField.passwordPattern} onChange={(event) => onFieldChange(selectedField.name, { passwordPattern: event.target.value })} helperText={t("Optional pattern that passwords must match.")} fullWidth />}
                                            </Stack></AccordionDetails>
                                        </Accordion>
                                        <RuleBuilder rules={selectedField.rules} fields={state.fields.filter((field) => field.name !== selectedField.name)} onChange={(rules) => onFieldRulesChange(selectedField.name, rules)} />
                                    </>
                                ) : selectedStep && typeof selectedStepIndex === "number" ? (
                                    <TranslatableInput
                                        label={t("Step title")}
                                        value={selectedStep.title || `Step ${selectedStepIndex + 1}`}
                                        suggestedKey={translationBase(state, `steps.${selectedStepIndex + 1}.title`)}
                                        defaultPublic={true}
                                        showTranslationKey={advancedOpen}
                                        helperText={String(t("This text is rendered in the step navigation and can be saved as a public translation key."))}
                                        onChange={(title) => onStepChange(selectedStepIndex, { title })}
                                    />
                                ) : null}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                ) : (
                    <Box className="service-builder__empty-properties">
                        <Typography variant="body2">{t("Select a field or step to edit its properties.")}</Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default PropertyPanel;
